import { Injectable } from "@angular/core";
import { Apollo } from "apollo-angular";
import { Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { StorageService } from "../services/storage.service";
import * as admin from "./role-querry";
import * as facility from "./facility-query";
import { getUsersByFacilityCode } from "./users-graphql";
import { StorageKey } from "../services/storage.model";
import {
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
} from "@angular/common/http";
import { ToastrService } from "ngx-toastr";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: "root",
})
export class AdminService {
  app = "tplmis";
  auth = "authentication";
  item = "item-configuration";
  private searchable$: Observable<any>;
  token: string;
  user: any;
  principal: any;
  facility_code: any;

  constructor(
    private apollo: Apollo,
    private storeSvc: StorageService,
    private http: HttpClient,
    private httpClient: HttpClient,
    private toastr: ToastrService,
    handler: HttpBackend
  ) {
    this.httpClient = new HttpClient(handler);
    this.token = localStorage.getItem("currentClient");
    if (this.token) {
      this.user = this.storeSvc.read(StorageKey.CURRENT_USER);
      if (this.user) {
        this.principal = this.user.principal;
      }
    }
  }

  public areaSearch(payload: string) {
    return this.apollo.watchQuery<any>({
      query: admin.residence,
      variables: {
        input: payload,
      },
    }).valueChanges;
  }

  handleError(error: HttpErrorResponse) {
    return throwError(error);
  }

  public saveTile(tile) {
    return this.apollo.mutate({
      mutation: admin.saveTile,
      variables: {
        tileDto: tile,
      },
    });
  }

  changePassword(param: any): Observable<any> {
    return this.http
      .post("/api/user/changePassword", param)
      .pipe(catchError(this.handleError));
  }

  allRoles() {
    return this.apollo.watchQuery<any>({
      query: admin.roles,
      fetchPolicy: "no-cache",
    }).valueChanges;
  }

  facilityTypes() {
    return this.apollo.watchQuery<any>({
      query: facility.facilityTypes,
      fetchPolicy: "no-cache",
    }).valueChanges;
  }

  allFacilities() {
    return this.apollo.watchQuery<any>({
      query: admin.facilities,
      fetchPolicy: "no-cache",
    }).valueChanges;
  }

  public getDepartmentsFacility() {
    return this.apollo.subscribe({
      query: admin.departments,
      variables: {
        facilityCode: this.facility_code,
      },
    });
  }

  public assignAllPermissions(roleDto) {
    return this.apollo.mutate({
      mutation: admin.assignPermissions,
      variables: {
        RoleDto: roleDto,
      },
    });
  }

  public updateFacility(payload) {
    return this.apollo.mutate({
      mutation: admin.updateFacilityLocation,
      variables: {
        facilityId: payload.facilityId,
        locationId: payload.locationId,
      },
    });
  }

  public saveUser(user) {
    return this.apollo.mutate({
      mutation: admin.saveUser,
      fetchPolicy: "no-cache",
      variables: {
        userAccountDto: user,
      },
    });
  }

  public updateUserAccount(user) {
    return this.apollo.mutate({
      mutation: admin.updateUser,
      fetchPolicy: "no-cache",
      variables: {
        userAccountDto: user,
      },
    });
  }

  public search(queryString: string) {
    return this.apollo.subscribe({
      query: admin.searchByFacilityNameOrCode,
      variables: {
        searchParam: queryString,
      },
    });
  }

  public getAll() {
    return this.apollo.subscribe({
      query: admin.roles,
    });
  }

  public getAllPermissionCategories() {
    return this.apollo.subscribe({
      query: admin.permissions,
    });
  }

  public loadAllPermissions() {
    return this.apollo.subscribe({
      query: admin.loadAllPermissions,
    });
  }

  public updateRole(updateRoleDto) {
    return this.apollo.mutate({
      mutation: admin.updateRole,
      variables: {
        RoleDto: updateRoleDto,
      },
    });
  }

  public getAllTiles() {
    return this.apollo.subscribe({
      query: admin.getAllTiles,
    });
  }

  public createRole(roleDto) {
    return this.apollo.mutate({
      mutation: admin.saveRole,
      variables: {
        RoleDto: roleDto,
      },
    });
  }

  public loadTiles() {
    return this.apollo.subscribe({
      query: admin.getAllTiles,
    });
  }

  public getAllByPermissionCategoryName(name: string) {
    return this.apollo.subscribe({
      query: admin.loadPermissionsByCategoryName,
      variables: {
        permissionCategoryName: name,
      },
    });
  }

  usersByFacility() {
    return this.apollo.watchQuery<any>({
      query: getUsersByFacilityCode,
      variables: {
        input: this.facility_code,
      },
      fetchPolicy: "no-cache",
    }).valueChanges;
  }

  users(): Observable<any> {
    return this.http.get("/api/user/users").pipe(catchError(this.handleError));
  }

  economic(): Observable<any> {
    return this.http
      .get("/api/economic-activities")
      .pipe(catchError(this.handleError));
  }

  memberPositions(): Observable<any> {
    return this.http
      .get("/api/group-member-positions")
      .pipe(catchError(this.handleError));
  }

  academicPosition(): Observable<any> {
    return this.http
      .get("/api/group-member-academic")
      .pipe(catchError(this.handleError));
  }

  sectors(): Observable<any> {
    return this.http.get("/api/sectors").pipe(catchError(this.handleError));
  }

  loanSummary(param?: any): Observable<any> {
    const page = "?adminAreaId=" + param;
    return this.http
      .get("/api/summary/loanDashboardSummary" + page)
      .pipe(catchError(this.handleError));
  }

  submittedApplications(param): Observable<any> {
    const page = "?page=0&size=100&adminAreaId=" + param;
    return this.http
      .get("/api/group/profile/pending")
      .pipe(catchError(this.handleError));
  }

  submittedCDOVerified(param): Observable<any> {
    const page = "?page=0&size=100&adminAreaId=" + param;
    return this.http
      .get("/api/group/profile/verified")
      .pipe(catchError(this.handleError));
  }

  groupPendingApplication(payload): Observable<any> {
    return this.http
      .get("/api/group/pending-per-backoffice?", {
        params: payload,
      })
      .pipe(catchError(this.handleError));
  }

  groupApprovedApplication(payload): Observable<any> {
    return this.http
      .get("/api/group/pending-per-status?", {
        params: payload,
      })
      .pipe(catchError(this.handleError));
  }

  submittedDCDOVerified(param): Observable<any> {
    const payload = {
      revewedByDcdo: true,
    };
    return this.http
      .post("/api/group/profile/any", payload)
      .pipe(catchError(this.handleError));
  }

  submittedDCDOPending(param): Observable<any> {
    const payload = {
      revewedByDcdo: false,
    };
    return this.http
      .post("/api/group/profile/any", payload)
      .pipe(catchError(this.handleError));
  }

  submittedDEDVerified(param): Observable<any> {
    const payload = {
      revewedByDed: true,
    };
    return this.http
      .post("/api/group/profile/any", payload)
      .pipe(catchError(this.handleError));
  }

  submittedApplicationsPage(param): Observable<any> {
    const page = "?page=0&size=100&adminAreaId=" + param;

    return this.http
      .get("/api/group/group-list-for-back-office" + page)
      .pipe(catchError(this.handleError));
  }

  nin(param): Observable<any> {
    return this.http
      .get("api/integration/nida?nida=" + param)
      .pipe(catchError(this.handleError));
  }

  economicBySector(param): Observable<any> {
    return this.http
      .get("/api/economic-activities/sector/" + param)
      .pipe(catchError(this.handleError));
  }

  groupType(): Observable<any> {
    return this.http
      .get("/api/applicant/grouptype")
      .pipe(catchError(this.handleError));
  }

  profile(): Observable<any> {
    return this.http
      .get("/api/group/groupProfile")
      .pipe(catchError(this.handleError));
  }

  menus(): Observable<any> {
    return this.http.get("/api/menus").pipe(catchError(this.handleError));
  }

  verify(param: any): Observable<any> {
    return this.http
      .get("/api/applicant/activate-account?activator=" + param)
      .pipe(catchError(this.handleError));
  }

  group(): Observable<any> {
    return this.http
      .get("/api/group/groupProfile")
      .pipe(catchError(this.handleError));
  }

  status(): Observable<any> {
    return this.http
      .get("/api/applicant/Statuses")
      .pipe(catchError(this.handleError));
  }

  saveGroup(param: any): Observable<any> {
    return this.http
      .post("/api/applicant/group/createGroupUser", param)
      .pipe(catchError(this.handleError));
  }

  saveQuestionare(param: any): Observable<any> {
    return this.http
      .post("/api/questionaire", param)
      .pipe(catchError(this.handleError));
  }

  updateQuestionare(param: any): Observable<any> {
    return this.http
      .put("/api/questionaire", param)
      .pipe(catchError(this.handleError));
  }

  listQuestionaire(): Observable<any> {
    return this.http
      .get("/api/questionaire")
      .pipe(catchError(this.handleError));
  }

  questionnareBySector(param: any): Observable<any> {
    return this.http
      .get("/api/questionaire/sector/" + param)
      .pipe(catchError(this.handleError));
  }

  loanEmploymentByApplicant(param: any): Observable<any> {
    return this.http
      .get(
        "/api/loanEmployment/loanEmployment/byLoanApplication?applicationId=" +
          param
      )
      .pipe(catchError(this.handleError));
  }

  saveGroupEconomic(param: any): Observable<any> {
    return this.http
      .post("/api/group/saveGroupEconomicActivity", param)
      .pipe(catchError(this.handleError));
  }

  saveLoanEmployment(param: any): Observable<any> {
    return this.http
      .post("/api/loanEmployment/loanEmployment", param)
      .pipe(catchError(this.handleError));
  }

  nidaQn(param: any): Observable<any> {
    return this.http
      .post("/api/quiz/integration/nida", param)
      .pipe(catchError(this.handleError));
  }

  submitApplication(param: any): Observable<any> {
    return this.http
      .post("/api/group/saveGroupProfileStatus", param)
      .pipe(catchError(this.handleError));
  }

  approveProfile(param: any): Observable<any> {
    return this.http
      .get("/api/group/approveGroup/" + param)
      .pipe(catchError(this.handleError));
  }

  submitProfile(param: any): Observable<any> {
    return this.http
      .get("/api/group/submitGroupProfile/" + param)
      .pipe(catchError(this.handleError));
  }

  saveGroupMembers(param: any): Observable<any> {
    return this.http
      .post("/api/group-members", param)
      .pipe(catchError(this.handleError));
  }

  changePassWord(param: any): Observable<any> {
    return this.http
      .post("/api/user/changePassword", param)
      .pipe(catchError(this.handleError));
  }

  approveGroupMember(param: any): Observable<any> {
    return this.http
      .post("/api/approve-group-members", param)
      .pipe(catchError(this.handleError));
  }

  saveGroupProfile(param: any): Observable<any> {
    return this.http
      .post("/api/group/saveGroupProfile", param)
      .pipe(catchError(this.handleError));
  }

  saveGroupMember(param: any): Observable<any> {
    return this.http
      .post("/api/group/group-members", param)
      .pipe(catchError(this.handleError));
  }

  saveGroupAttachment(param: any): Observable<any> {
    return this.http
      .post("/api/group/saveGroupProfileAttachment", param)
      .pipe(catchError(this.handleError));
  }

  saveDelegation(param: any): Observable<any> {
    return this.http
      .post("/api/deligation/-save-deligation", param)
      .pipe(catchError(this.handleError));
  }

  forget(param: any): Observable<any> {
    return this.http
      .get("/api/applicant/forget-password/" + param)
      .pipe(catchError(this.handleError));
  }

  memberByProfile(param: any): Observable<any> {
    return this.http
      .get("/api/group-member-by-profile/" + param)
      .pipe(catchError(this.handleError));
  }

  seachMember(param: any): Observable<any> {
    return this.http
      .get("/api/group-members/searchKey?searchKey=" + param)
      .pipe(catchError(this.handleError));
  }

  leaderByProfile(param: any): Observable<any> {
    return this.http
      .get("/api/group-member-leader-by-profile/" + param)
      .pipe(catchError(this.handleError));
  }

  groupEconomic(param: any): Observable<any> {
    return this.http
      .get("/api/group/groupEconomicActivity?profileId=" + param)
      .pipe(catchError(this.handleError));
  }

  deleteGroupActivity(param: any): Observable<any> {
    return this.http
      .get("/api/group/deleteGroupEconomicActivity?id=" + param)
      .pipe(catchError(this.handleError));
  }

  deleteGroupProfile(param: any): Observable<any> {
    return this.http
      .get("/api/group/deleteGroupProfileAttachment?id=" + param)
      .pipe(catchError(this.handleError));
  }

  deleteGroup(param: any): Observable<any> {
    return this.http
      .get("/api/group/deleteGroup?id=" + param)
      .pipe(catchError(this.handleError));
  }

  deleteMember(param: any): Observable<any> {
    return this.http
      .delete("/api/delete/group-members/" + param)
      .pipe(catchError(this.handleError));
  }

  deleteQuestionare(param: any): Observable<any> {
    return this.http
      .delete("/api/questionaire/delete/" + param)
      .pipe(catchError(this.handleError));
  }

  approveGroupMembers(param: any): Observable<any> {
    return this.http
      .get("/api/group-members-for-approval/" + param)
      .pipe(catchError(this.handleError));
  }

  approveMembers(): Observable<any> {
    return this.http
      .get("/api/approve-group-members")
      .pipe(catchError(this.handleError));
  }

  loanDashboard(param?: any): Observable<any> {
    const page = "?adminAreaId=" + param;
    return this.http
      .get("/api/dashboard/dash-board-data" + page)
      .pipe(catchError(this.handleError));
  }

  groupStatus(param: any): Observable<any> {
    return this.http
      .get("/api/group/GroupProfileStatus?profileId=" + param)
      .pipe(catchError(this.handleError));
  }

  groupMembersApproval(param: any): Observable<any> {
    return this.http
      .get("/api/group-members-for-approval/" + param)
      .pipe(catchError(this.handleError));
  }

  groupAttachment(param: any): Observable<any> {
    return this.http
      .get("/api/group/groupProfileAttachment?profileId=" + param)
      .pipe(catchError(this.handleError));
  }

  resend(param: any): Observable<any> {
    return this.http
      .get("/api/applicant/resent-activation-link/" + param)
      .pipe(catchError(this.handleError));
  }

  roles(): Observable<any> {
    return this.http
      .get("/api/roles/getAllRoles")
      .pipe(catchError(this.handleError));
  }

  groupApproveMembers(param): Observable<any> {
    return this.http
      .get("/api/group/group-list-with-members-for-approval", {
        params: param,
      })
      .pipe(catchError(this.handleError));
  }

  delegation(): Observable<any> {
    return this.http
      .get("/api/deligation/deligation")
      .pipe(catchError(this.handleError));
  }

  userDelegation(): Observable<any> {
    return this.http
      .get("/api/user/delegetee")
      .pipe(catchError(this.handleError));
  }

  groupAttachmentTypes(): Observable<any> {
    return this.http
      .get("/api/document-types")
      .pipe(catchError(this.handleError));
  }

  searchArea(param): Observable<any> {
    return this.http
      .get("api/applicant/area/search-administrative-area?searchKey=" + param)
      .pipe(catchError(this.handleError));
  }

  endDelegation(param): Observable<any> {
    return this.http
      .get("api/deligation/end-deligation/" + param)
      .pipe(catchError(this.handleError));
  }

  searchAdmin(param): Observable<any> {
    return this.httpClient
      .get("api/applicant/area/search-administrative-area?searchKey=" + param)
      .pipe(catchError(this.handleError));
  }

  getAdminAreas(): Observable<any> {
    return this.httpClient
      .get("api/applicant/area/get-admin-area-self-reg-level")
      .pipe(catchError(this.handleError));
  }

  getAdminByParentId(param): Observable<any> {
    return this.httpClient
      .get("api/applicant/area/search-administrative-area-level?id=" + param.id)
      .pipe(catchError(this.handleError));
  }

  getUserTypes(): Observable<any> {
    return this.http.get("api/user-type").pipe(catchError(this.handleError));
  }

  getUserPositions(): Observable<any> {
    return this.http.get("api/position").pipe(catchError(this.handleError));
  }
}
